import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import clsx from "clsx";
import { useSlate, ReactEditor } from "slate-react";
import { useTranslation } from "react-i18next";
import Portal from "../../../../Portal";
import { getMongoObjectId, getApiName, format } from "../../../../../utils";
import styled from "styled-components";

const VariableChooserContainer = styled.div`
  position: absolute;
  width: 200px;
  z-index: 999;
  box-shadow: 1px 2px 10px 3px rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  background: #fff;
`;
const VariableChooserTip = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: ${(props) =>
    props.direction === "down" ? "10px 10px 0 10px" : "0 10px 10px"};
  border-color: ${(props) =>
    props.direction === "down"
      ? "#FFF transparent transparent transparent"
      : "transparent transparent #36505d"};
  top: ${(props) => (props.direction === "up" ? "-10px" : "")};
  left: 8px;
`;
const VariableItemTitle = styled.div`
  height: 25px;
  line-height: 25px;
  padding: 0px 10px;
  background: #36505d;
  color: #ddd;
  font-size: 9pt;
`;
const VariableItemOption = styled.div`
  padding: 4px 10px;
  text-align: center;
  font-size: 9pt;
  &.selected {
    background: #fff9b6;

    &.variable-chooser-exists {
      span:before {
        content: ">";
        font-size: 8pt;
        padding-right: 3px;
        position: relative;
        top: -1px;
        font-weight: bold;
      }
    }
  }
`;

export function VariablesMenu({
  variables,
  at,
  search,
  onVariableClick,
  onAddVariable,
}) {
  const { t } = useTranslation();
  const ref = useRef();
  const editor = useSlate();
  const [showOnTop, setShowOnTop] = useState();

  useEffect(() => {
    if (at && variables.length > 0) {
      const el = ref.current;
      const domRange = ReactEditor.toDOMRange(editor, at);
      const rect = domRange.getBoundingClientRect();

      if (el) {
        const menuHeight = el.offsetHeight;
        const shouldShowOnTop = rect.top + menuHeight > 700;
        const top = shouldShowOnTop
          ? rect.top - menuHeight - 10
          : rect.top + 24;
        setShowOnTop(shouldShowOnTop);
        el.style.top = `${top + window.pageYOffset}px`;
        el.style.left = `${rect.left + window.pageXOffset}px`;
      }
    }
  }, [variables.length, editor, at]);

  const handleAddVariable = async ({
    searchTerm,
    participantId,
    participantIndex,
  }) => {
    const newVar = {
      _id: getMongoObjectId(),
      type: "text",
      value: "",
      name: search.replace(/_/gi, " "),
      apiName: getApiName(search, participantIndex),
    };
    await onAddVariable({ participantId, variable: newVar });
    onVariableClick(editor, { participantId, ...newVar });
  };

  return (
    <Portal selectorId="#modals">
      <VariableChooserContainer
        className={clsx("variable-chooser", !at && "d-none")}
        ref={ref}
        contentEditable={false}
      >
        {!showOnTop && (
          <VariableChooserTip className="variable-chooser-tip" direction="up" />
        )}
        <div className="variable-chooser-content">
          {variables.map((variable, index) => {
            return (
              <VariableItemContainer
                key={`${variable.entity}-${variable.participantId}-${index}`}
                title={
                  variable.entity === "contract"
                    ? t("editor.variables.popup_properties")
                    : variable.label
                }
                icon="circle"
                iconClassName={
                  variable.entity === "contract"
                    ? "participantproperty"
                    : `participant${variable.participantIndex}-color`
                }
                participantId={variable.participantId}
                participantIndex={variable.participantIndex}
                options={variable.options}
                matched={variable.matched}
                onVariableClick={onVariableClick}
                onAddVariable={handleAddVariable}
                isContractProperty={variable.entity === "contract"}
                searchTerm={search}
              />
            );
          })}
          {showOnTop && (
            <VariableChooserTip
              className="variable-chooser-tip"
              direction="down"
            />
          )}
        </div>
      </VariableChooserContainer>
    </Portal>
  );
}

function VariableItemContainer({
  participantId,
  participantIndex,
  isContractProperty,
  title,
  icon,
  iconClassName,
  options = [],
  searchTerm,
  matched,
  onVariableClick,
  onAddVariable,
}) {
  const { t } = useTranslation();
  const editor = useSlate();

  const handleVariableClick = (variable) => () => {
    onVariableClick(editor, { ...variable, participantId, participantIndex });
  };

  const handleAddVariable = () => {
    onAddVariable({ searchTerm, participantId, participantIndex });
  };

  return (
    <div key={participantId}>
      <VariableItemTitle className="variable-chooser-part">
        <i className={`fas fa-${icon} ${iconClassName}`}></i> {title}
      </VariableItemTitle>
      {options.map((option, i) => {
        return (
          <VariableItemOption
            key={`${participantId}-${option.apiName}-${i}`}
            className="variable-action variable-chooser-exists pointer"
          >
            <span
              className="as-link"
              data-type={option.type}
              data-name={option.name}
              data-apiname={option.apiName}
              data-participant={isContractProperty ? "property" : participantId}
              data-id={option.id}
              data-value={option.value}
              data-action="use-variable"
              data-index={isContractProperty ? "property" : participantIndex}
              onMouseDown={handleVariableClick(option)}
            >
              {option.type === "constant" && (
                <strong>{t("contracts_.constants.property")}: </strong>
              )}
              {option.name}
            </span>
          </VariableItemOption>
        );
      })}
      {!matched && !isContractProperty && (
        <VariableItemOption className="variable-action variable-chooser-notexists">
          <span
            className="as-link break-word"
            data-participant={participantId}
            data-index={participantIndex}
            data-action="add-variable"
            onMouseDown={handleAddVariable}
          >
            {t("editor.variables.popup_add")} "{searchTerm}"
          </span>
        </VariableItemOption>
      )}
    </div>
  );
}
